import { batch } from 'react-redux';

// project imports
import gestopAPI from 'utils/gestopAPI';
import { dispatch } from 'store/index';
import { errorProcessor } from 'utils/processors';
import { crmPartnershipTypeActions } from './slice';
import capabilities from './capabilities';
import createImportApi from 'utils/importAPI';

const partnershipTypeImportApi = createImportApi({
    actions: crmPartnershipTypeActions,
    capabilities,
    endpoint: 'crm/partnershiptypes',
    templateFileName: 'PartnershipTypeImportTpl'
});
export const ApiPartnershipTypesImportCollection = partnershipTypeImportApi.importCollection;
export const ApiPartnershipTypesDownloadImportModelItem = partnershipTypeImportApi.downloadImportModel;

export const ApiPartnershipTypesGetCollection = async ({
    filters = [],
    orderBy = [],
    groupBy = [],
    page = 1,
    limit = 10,
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmPartnershipTypeActions.startLoading());
    }

    await gestopAPI
        .post('crm/partnershiptypes/list', { filters, orderBy, groupBy, page, limit, includeSafeDeleted }, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmPartnershipTypeActions.setList(response?.data?.list));
                    dispatch(crmPartnershipTypeActions.setCount(response?.data?.count));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data?.list);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmPartnershipTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiPartnershipTypesCountCollection = async ({
    filters = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post('crm/partnershiptypes/count', { filters, includeSafeDeleted }, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.count);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiPartnershipTypesPostCollection = async ({
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmPartnershipTypeActions.startLoading());
    }

    await gestopAPI
        .post('crm/partnershiptypes', data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmPartnershipTypeActions.insertItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmPartnershipTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiPartnershipTypesDeleteCollection = async ({
    ids = [],
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmPartnershipTypeActions.startLoading());
    }

    await gestopAPI
        .post('crm/partnershiptypes/safedelete', ids, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmPartnershipTypeActions.deleteItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmPartnershipTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiPartnershipTypesExportCollection = async ({
    exportType = 'allRows',
    format = 'pdf',
    columns = [],
    filters = [],
    orderBy = [],
    groupBy = [],
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(crmPartnershipTypeActions.startExporting());
    }

    await gestopAPI
        .post(
            'crm/partnershiptypes/export',
            { exportType, format, columns, filters, orderBy, groupBy },
            { ...config, responseType: 'blob' }
        )
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(crmPartnershipTypeActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(crmPartnershipTypeActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmPartnershipTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiPartnershipTypesPutItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmPartnershipTypeActions.startLoading());
    }

    await gestopAPI
        .put(`crm/partnershiptypes/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmPartnershipTypeActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmPartnershipTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiPartnershipTypesPatchItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmPartnershipTypeActions.startLoading());
    }

    await gestopAPI
        .patch(`crm/partnershiptypes/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'application/merge-patch+json' } // Specific content type for PATCH requests
        })
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmPartnershipTypeActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmPartnershipTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiPartnershipTypesDeleteItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmPartnershipTypeActions.startLoading());
    }

    await gestopAPI
        .post(`crm/partnershiptypes/safedelete/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmPartnershipTypeActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmPartnershipTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiPartnershipTypesRestoreDeletedItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmPartnershipTypeActions.startLoading());
    }

    await gestopAPI
        .post(`crm/partnershiptypes/restoresafedeleted/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmPartnershipTypeActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmPartnershipTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiPartnershipTypesGetItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmPartnershipTypeActions.startLoading());
    }

    await gestopAPI
        .get(`crm/partnershiptypes/${id}`, config)
        .then((response) =>
            batch(() => {
                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmPartnershipTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmPartnershipTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};
